import { useEffect, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { CheckCircleIcon } from "lucide-react";
import { userService, profileService } from "../../services";

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { mode } = useParams();
  console.log("mode", mode);

  // extracts token and email from Invitation URL
  useEffect(() => {
    const path = location.pathname;
    const lastSegment = path.split("/").pop();

    if (lastSegment && lastSegment.includes("&&&")) {
      const [token, email] = lastSegment.split("&&&");
      const decodedEmail = decodeURIComponent(email);

      sessionStorage.setItem("invitationToken", token);
      sessionStorage.setItem("invitationEmail", decodedEmail);
      setEmail(decodedEmail);

      console.log("Token stored:", token);
      console.log("Email stored:", decodedEmail);
    } else {
      // Fallback: get email from sessionStorage
      const storedEmail = sessionStorage.getItem("invitationEmail") || "";
      setEmail(storedEmail);
      console.log("Fallback email from sessionStorage:", storedEmail);
    }
  }, [location]);

  if (!["invitation", "reassign"].includes(mode)) {
    return <Navigate to="/not-found" />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const result = await userService.login(email, password);
      console.log("Login successful:", result);

      const userId = result?.Data?._id;
      if (!userId) throw new Error("User ID not found in login response");

      sessionStorage.setItem("userId", userId);

      const profilesResponse = await profileService.getUserProfiles(userId);
      const profiles = profilesResponse?.Data || [];

      // Extract only _id and name
      const simplifiedProfiles = profiles.map((profile) => ({
        profileId: profile._id,
        name: profile.name,
      }));

      sessionStorage.setItem(
        "userProfiles",
        JSON.stringify(simplifiedProfiles)
      );
      console.log("Stored user profiles:", simplifiedProfiles);

      navigate(`/${mode}/login/profileselect/`);
    } catch (error) {
      console.error("Login or profile fetch failed:", error);
      alert("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-teal-500 relative">
      {/* Background Bubbles */}
      <div className="absolute w-72 h-72 bg-white opacity-10 rounded-full top-10 left-10"></div>
      <div className="absolute w-96 h-96 bg-white opacity-10 rounded-full bottom-10 right-10"></div>
      <div className="absolute w-96 h-96 bg-white opacity-10 rounded-full top-12 center "></div>

      <div className="text-center text-white mb-6 z-10">
        <h1 className="text-4xl font-bold">Welcome Aboard!</h1>
        <p className="text-md">Fill in your details to join.</p>
      </div>
      <div className="bg-white p-8 rounded-2xl shadow-lg w-96 z-10">
        <h2 className="text-xl font-semibold text-center mb-4">Login</h2>
        <form onSubmit={handleLogin}>
          <div className="mb-4">
            <label className="block text-sm font-medium">Email Address</label>
            <div className="relative mt-1">
              <Input
                type="email"
                value={email}
                disabled
                className="bg-gray-100 cursor-not-allowed"
              />
              <CheckCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium">Password</label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />

              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEye /> : <FaEyeSlash />}
              </button>
            </div>
          </div>
          <div className="flex items-center mb-4">
            <input type="checkbox" id="remember" className="mr-2" />
            <label htmlFor="remember" className="text-sm">
              Remember Me
            </label>
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Logging in..." : "Login"}
          </Button>
        </form>
        <p className="text-center text-sm text-gray-500 mt-4 cursor-pointer hover:underline">
          Forgot Password?
        </p>
      </div>
    </div>
  );
}
